/**
 * Advanced Question Bank: Domain 3 — Spatial & Visual Reasoning (التصور البصري والمكاني)
 * 15 Psychometrically Rigorous Generative Template Families (Visual Processing Gv / Mental Rotation)
 */

import { renderOptionSvg, renderIsometricCube, renderCubeNet, DrawShapes, wrapSvg } from './svg-builder.js';

export const ADVANCED_SPATIAL_TEMPLATES = [
  // 1. Isometric Cube 90-Degree Y-Axis Rotation
  {
    id: 'spatial_isometric_rotation_90',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // 4 distinct symbols for faces: Front (Left), Right, Back, Left, Top, Bottom
      const syms = ['▲', '■', '●', '★', '◆', '✚'];
      const chosen = prng.sample(syms, 4);
      const [fLeft, fRight, fBack, fHidden] = chosen;
      const topSym = '▲';

      // Cube 1 views: Top = topSym, Left = fLeft, Right = fRight
      const cube1Svg = renderIsometricCube(topSym, fLeft, fRight, 130);

      // Rotating 90° Clockwise around vertical axis:
      // What was on the Right moves to the Left!
      // What was on the Back moves to the Right!
      // Top remains topSym!
      const rotatedLeft = fRight;
      const rotatedRight = fBack;

      const correctCubeSvg = renderIsometricCube(topSym, rotatedLeft, rotatedRight, 105);

      // Distractors: wrong rotations, flipped top, or inverted faces
      const dist1 = renderIsometricCube(topSym, fRight, fLeft, 105); // Left & right swapped
      const dist2 = renderIsometricCube('■', rotatedLeft, rotatedRight, 105); // Wrong top
      const dist3 = renderIsometricCube(topSym, fLeft, fBack, 105); // Failed rotation

      const all = prng.shuffle([
        { svg: correctCubeSvg, isCorrect: true, val: 'rot90' },
        { svg: dist1, isCorrect: false, val: 'swapped' },
        { svg: dist2, isCorrect: false, val: 'wrong_top' },
        { svg: dist3, isCorrect: false, val: 'no_shift' }
      ]);
      const correctIndex = all.findIndex(item => item.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: it.svg,
        value: it.val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا تم تدوير المكعب ثلاثي الأبعاد الموضح في الشكل بمقدار 90 درجة باتجاه عقارب الساعة حول محوره الرأسي، مع العلم بأن الوجه الخلفي غير المرئي يحمل الرمز (${fBack}). أي من الخيارات التالية يمثل المنظور الناتج بدقة؟`,
        questionText_en: `The 3D cube shown is rotated 90° clockwise around its vertical Y-axis. Given that the occluded rear face carries the symbol (${fBack}), which option represents the exact resulting isometric perspective?`,
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${cube1Svg}</div>`,
        options,
        correctIndex,
        explanation_ar: `عند الدوران 90 درجة مع عقارب الساعة حول المحور الرأسي، ينتقل الوجه الأيمن (${fRight}) ليصبح في الوجه الأيسر، وينتقل الوجه الخلفي (${fBack}) ليصبح في الوجه الأيمن، بينما يظل الوجه العلوي (${topSym}) ثابتًا. هذا المنظور يطابق حصريًا الخيار (${options[correctIndex].id}).`,
        explanation_en: `Rotating 90° clockwise maps the previous right face (${fRight}) to the left position and brings the rear face (${fBack}) into the right position, leaving the top face (${topSym}) unchanged, isolating (${options[correctIndex].id}).`
      };
    }
  },

  // 2. Hidden Cube Count in Multi-Level Isometric Stack
  {
    id: 'spatial_cube_stack_count',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Stack dimensions: 3 columns with heights h1, h2, h3
      const h1 = prng.int(2, 4);
      const h2 = prng.int(1, 3);
      const h3 = prng.int(2, 4);
      const totalCubes = h1 + h2 + h3;

      // Draw isometric representation of the three vertical pillars
      const drawPillars = (size = 180) => {
        let out = '';
        const cx = size / 2;
        const cy = size / 2;
        const unit = 24;
        // Draw 3 columns isometric
        const cols = [
          { x: cx - 40, y: cy + 20, h: h1, col: '#38bdf8' },
          { x: cx, y: cy + 40, h: h2, col: '#818cf8' },
          { x: cx + 40, y: cy + 20, h: h3, col: '#ec4899' }
        ];

        cols.forEach(c => {
          for (let l = 0; l < c.h; l++) {
            const py = c.y - l * unit;
            out += `<polygon points="${c.x},${py - 12} ${c.x + 20},${py - 2} ${c.x},${py + 8} ${c.x - 20},${py - 2}" fill="${c.col}" fill-opacity="0.3" stroke="currentColor" stroke-width="1.5"/>`;
            out += `<polygon points="${c.x - 20},${py - 2} ${c.x},${py + 8} ${c.x},${py + 22} ${c.x - 20},${py + 12}" fill="${c.col}" fill-opacity="0.6" stroke="currentColor" stroke-width="1.5"/>`;
            out += `<polygon points="${c.x},${py + 8} ${c.x + 20},${py - 2} ${c.x + 20},${py + 12} ${c.x},${py + 22}" fill="${c.col}" fill-opacity="0.8" stroke="currentColor" stroke-width="1.5"/>`;
          }
        });
        return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-stack', 'Isometric Cube Stack');
      };

      const correctVal = totalCubes;
      const distractors = [correctVal - 1, correctVal + 1, correctVal + 2];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${v} مكعبات`,
        text_en: `${v} cubes`,
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `كم عدد المكعبات المتطابقة الإجمالي المكون لهذا المجسم ثلاثي الأبعاد، مع الأخذ في الحسبان المكعبات الداعمة غير المرئية في الأسفل؟`,
        questionText_en: `What is the total number of identical cubic blocks forming this 3D structure, including the occluded supporting blocks underneath?`,
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${drawPillars(200)}</div>`,
        options,
        correctIndex,
        explanation_ar: `يتكون المجسم من 3 أعمدة رأسية بارتفاعات: (${h1}) مكعبات في العمود الأيسر + (${h2}) في الأوسط + (${h3}) في الأيمن. إجمالي المكعبات = ${h1} + ${h2} + ${h3} = ${correctVal} مكعبًا (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Decomposing into vertical pillars yields column heights of ${h1}, ${h2}, and ${h3}. Summing them establishes exactly ${correctVal} total cubes (${options[correctIndex].id}).`
      };
    }
  },

  // 3. Orthographic Top-Down Plan Projection
  {
    id: 'spatial_orthographic_top_view',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // 2x2 grid with varying block heights: e.g. [[2, 1], [0, 3]]
      const hTL = prng.int(1, 3);
      const hTR = prng.int(1, 3);
      const hBL = prng.int(0, 2);
      const hBR = prng.int(1, 3);

      const drawTopPlan = (grid, size = 100) => {
        let out = '';
        const s = size * 0.42;
        const offset = (size - s * 2) / 2;
        const coords = [
          [0, 0, grid[0][0]], [1, 0, grid[0][1]],
          [0, 1, grid[1][0]], [1, 1, grid[1][1]]
        ];
        coords.forEach(([c, r, val]) => {
          const x = offset + c * s;
          const y = offset + r * s;
          const bg = val > 0 ? '#38bdf8' : 'rgba(255, 255, 255, 0.05)';
          const opacity = val > 0 ? (0.2 + val * 0.25).toFixed(2) : 0.05;
          out += `<rect x="${x}" y="${y}" width="${s}" height="${s}" fill="#38bdf8" fill-opacity="${opacity}" stroke="currentColor" stroke-width="1.8"/>`;
          out += `<text x="${x + s / 2}" y="${y + s / 2 + 6}" text-anchor="middle" font-size="16" font-weight="700" fill="currentColor">${val}</text>`;
        });
        return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-ortho', 'Top-Down Plan');
      };

      const trueGrid = [[hTL, hTR], [hBL, hBR]];
      const candidateDists = [
        [[hTR, hTL], [hBR, hBL]],
        [[hBL, hBR], [hTL, hTR]],
        [[hTL + 1, hTR], [hBL, hBR]],
        [[hTL, hTR + 1], [hBL, hBR]],
        [[hTL, hTR], [hBL + 1, hBR]],
        [[hTL, hTR], [hBL, hBR + 1]]
      ];
      const seenGrids = new Set([JSON.stringify(trueGrid)]);
      const uniqueDistractors = [];
      for (const d of candidateDists) {
        const key = JSON.stringify(d);
        if (!seenGrids.has(key)) {
          seenGrids.add(key);
          uniqueDistractors.push(d);
          if (uniqueDistractors.length === 3) break;
        }
      }

      const all = prng.shuffle([
        { g: trueGrid, isCorrect: true },
        ...uniqueDistractors.map(d => ({ g: d, isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: drawTopPlan(it.g, 105),
        value: JSON.stringify(it.g)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا نظرنا إلى مجسم مكون من شبكة أعمدة 2×2 بأعمدة ذات ارتفاعات: أعلى اليسار (${hTL})، أعلى اليمين (${hTR})، أسفل اليسار (${hBL})، أسفل اليمين (${hBR}). أي المخططات الرأسية (Top-Down View) يمثل المسقط الهندسي الصحيح مع أعداد الطبقات؟`,
        questionText_en: `Viewing a 2x2 pillar assembly with heights: top-left (${hTL}), top-right (${hTR}), bottom-left (${hBL}), bottom-right (${hBR}). Which top-down orthographic plan accurately displays the layer distribution?`,
        options,
        correctIndex,
        explanation_ar: `المسقط الأفقي من الأعلى يعرض المربعات بنفس إحداثياتها المكانية المباشرة: الصف العلوي يحتوي على [${hTL}, ${hTR}] والصف السفلي على [${hBL}, ${hBR}]. الخيار المطابق هو (${options[correctIndex].id}).`,
        explanation_en: `Direct top-down projection preserves planar matrix coordinates, mapping the top row to [${hTL}, ${hTR}] and bottom row to [${hBL}, ${hBR}], verifying (${options[correctIndex].id}).`
      };
    }
  },

  // 4. Paper Folding & Hole Punching Visual Deduction
  {
    id: 'spatial_paper_hole_punch',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Square paper folded in half (Left to Right), then Top to Bottom (Quarter square)
      // Hole punched in bottom-right corner of the folded quarter square
      // When unfolded, 4 holes appear: in center (if corner was center) or symmetrically
      const punchLocations = ['center', 'outer_corners', 'edge_centers'];
      const chosenLoc = prng.pick(punchLocations);

      const drawUnfolded = (loc) => (size = 110) => {
        let out = `<rect x="10" y="10" width="${size - 20}" height="${size - 20}" rx="6" fill="none" stroke="currentColor" stroke-width="1.8"/>`;
        out += `<line x1="${size / 2}" y1="10" x2="${size / 2}" y2="${size - 10}" stroke="currentColor" stroke-dasharray="3,3" opacity="0.4"/>`;
        out += `<line x1="10" y1="${size / 2}" x2="${size - 10}" y2="${size / 2}" stroke="currentColor" stroke-dasharray="3,3" opacity="0.4"/>`;

        const r = 5.5;
        if (loc === 'center') {
          // 4 holes near center
          const d = 14;
          const cx = size / 2;
          const cy = size / 2;
          out += `<circle cx="${cx - d}" cy="${cy - d}" r="${r}" fill="#ef4444"/>`;
          out += `<circle cx="${cx + d}" cy="${cy - d}" r="${r}" fill="#ef4444"/>`;
          out += `<circle cx="${cx - d}" cy="${cy + d}" r="${r}" fill="#ef4444"/>`;
          out += `<circle cx="${cx + d}" cy="${cy + d}" r="${r}" fill="#ef4444"/>`;
        } else if (loc === 'outer_corners') {
          const d = 24;
          out += `<circle cx="${d}" cy="${d}" r="${r}" fill="#ef4444"/>`;
          out += `<circle cx="${size - d}" cy="${d}" r="${r}" fill="#ef4444"/>`;
          out += `<circle cx="${d}" cy="${size - d}" r="${r}" fill="#ef4444"/>`;
          out += `<circle cx="${size - d}" cy="${size - d}" r="${r}" fill="#ef4444"/>`;
        } else {
          // Edge centers
          const cx = size / 2;
          const cy = size / 2;
          const d = size / 2 - 20;
          out += `<circle cx="${cx}" cy="${cy - d}" r="${r}" fill="#ef4444"/>`;
          out += `<circle cx="${cx}" cy="${cy + d}" r="${r}" fill="#ef4444"/>`;
          out += `<circle cx="${cx - d}" cy="${cy}" r="${r}" fill="#ef4444"/>`;
          out += `<circle cx="${cx + d}" cy="${cy}" r="${r}" fill="#ef4444"/>`;
        }
        return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-punch', 'Unfolded Holes');
      };

      const all = prng.shuffle([
        { loc: chosenLoc, isCorrect: true },
        { loc: chosenLoc === 'center' ? 'outer_corners' : 'center', isCorrect: false },
        { loc: chosenLoc === 'edge_centers' ? 'outer_corners' : 'edge_centers', isCorrect: false },
        { loc: 'single_hole', isCorrect: false }
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: it.loc === 'single_hole'
          ? renderOptionSvg((s) => `<rect x="${-s * 0.4}" y="${-s * 0.4}" width="${s * 0.8}" height="${s * 0.8}" rx="4" fill="none" stroke="currentColor"/><circle cx="0" cy="0" r="6" fill="#ef4444"/>`, 105, 'Single Hole')
          : drawUnfolded(it.loc)(105),
        value: it.loc
      }));

      const locDesc = chosenLoc === 'center'
        ? 'في الزاوية الداخلية المطوية (التي تمثل مركز الورقة الأصلي)'
        : (chosenLoc === 'outer_corners' ? 'في الزاوية الخارجية الحرة للورقة المطوية' : 'في منتصف حافة الطي المستقيمة');

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `طُويت ورقة مربعة الشكل مرتين متتاليتين (من اليسار لليمين، ثم من الأعلى للأسفل لتصبح ربع مربع)، ثم تم ثقبها ${locDesc}. عند إعادة فتح وفرد الورقة بالكامل، أي الأشكال التالية يمثل نمط الثقوب الصحيح؟`,
        questionText_en: `A square paper is folded twice (left-to-right, then top-to-bottom into a quadrant), and a hole is punched ${locDesc}. When unfolded completely, which pattern correctly shows the punched holes?`,
        options,
        correctIndex,
        explanation_ar: `طي الورقة إلى أربعة أرباع يعني أن كل ثقب سينعكس بالتناظر عبر محوري الطي (الأفقي والرأسي) ليظهر في الأرباع الأربعة بنفس الموقع النسبي، وهو ما يتطابق مع النمط الموضح في الخيار (${options[correctIndex].id}).`,
        explanation_en: `Bilateral folding creates dual-axis reflection symmetry across both fold axes, reproducing the punched hole in all 4 quadrants identically as seen in (${options[correctIndex].id}).`
      };
    }
  },

  // 5. Gear Transmission Rotation Direction & Velocity
  {
    id: 'spatial_gear_transmission_3d',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // 4 intermeshed gears in line: Gear 1 -> Gear 2 -> Gear 3 -> Gear 4
      // Adjacent meshed gears rotate in opposite directions
      // Even number of meshing: Gear 1 CW -> Gear 2 CCW -> Gear 3 CW -> Gear 4 CCW
      const startCW = prng.pick([true, false]);
      const dir1 = startCW ? 'مع عقارب الساعة (Clockwise)' : 'عكس عقارب الساعة (Counter-Clockwise)';
      const dir4 = startCW ? 'عكس عقارب الساعة (Counter-Clockwise)' : 'مع عقارب الساعة (Clockwise)';

      const drawGears = (size = 280) => {
        let out = '';
        const r = 24;
        const centers = [
          { x: 40, y: 50, col: '#38bdf8', label: '1' },
          { x: 86, y: 50, col: '#818cf8', label: '2' },
          { x: 132, y: 50, col: '#ec4899', label: '3' },
          { x: 178, y: 50, col: '#10b981', label: '4' }
        ];

        centers.forEach((c) => {
          out += `<circle cx="${c.x}" cy="${c.y}" r="${r}" fill="${c.col}" fill-opacity="0.25" stroke="${c.col}" stroke-width="2.5" stroke-dasharray="4,2"/>`;
          out += `<circle cx="${c.x}" cy="${c.y}" r="6" fill="#ffffff"/>`;
          out += `<text x="${c.x}" y="${c.y + 4}" text-anchor="middle" font-size="12" font-weight="800" fill="currentColor">${c.label}</text>`;
        });
        return wrapSvg(out, `0 0 220 100`, 'iq-svg-gears', 'Gear Train');
      };

      const options = [
        { id: 'A', text_ar: 'مع عقارب الساعة (Clockwise)', text_en: 'Clockwise', value: 'CW' },
        { id: 'B', text_ar: 'عكس عقارب الساعة (Counter-Clockwise)', text_en: 'Counter-Clockwise', value: 'CCW' },
        { id: 'C', text_ar: 'ثابت ولا يدور (Stationary)', text_en: 'Stationary', value: 'ST' },
        { id: 'D', text_ar: 'يتذبذب للأمام والخلف (Oscillating)', text_en: 'Oscillating', value: 'OSC' }
      ];

      const correctIndex = startCW ? 1 : 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في قطار التروس الميكانيكي المتشابك الموضح: إذا كان الترس رقم (1) يدور في اتجاه ${dir1}، ففي أي اتجاه يدور الترس الأخير رقم (4)؟`,
        questionText_en: `In the intermeshed gear train shown: if gear (1) rotates ${dir1}, in which direction does gear (4) rotate?`,
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${drawGears(240)}</div>`,
        options,
        correctIndex,
        explanation_ar: `كل ترسين متعشقين يدوران في اتجاهين متعاكسين: الترس 1 يدور ${dir1} ⬅️ الترس 2 يدور في الاتجاه المعاكس ⬅️ الترس 3 يدور في نفس اتجاه الترس 1 ⬅️ الترس 4 يدور في الاتجاه المعاكس (${dir4}). الإجابة الصحيحة هي (${options[correctIndex].id}).`,
        explanation_en: `Meshed gears invert rotation parity at each interface: Gear 1 -> Gear 2 (inverted) -> Gear 3 (original) -> Gear 4 (inverted), resolving decisively to (${options[correctIndex].id}).`
      };
    }
  },

  // 6. Cross-Sectional Geometric Plane Slice
  {
    id: 'spatial_cross_section_slice',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Shape: Solid Cone or Cylinder sliced at an angle
      const solid = prng.pick(['cylinder_oblique', 'cone_horizontal', 'cube_diagonal']);

      let questionText_ar = '';
      let questionText_en = '';
      let correctAns_ar = '';
      let correctAns_en = '';
      let explanation_ar = '';
      let explanation_en = '';

      if (solid === 'cylinder_oblique') {
        questionText_ar = 'إذا تم قطع أسطوانة دائرية مصمتة بواسطة مستوى مائل يقطع قاعدتيها دون أن يوازي محورها، فما هو الشكل الهندسي الدقيق لمقطع القطع العرضي الناتج؟';
        questionText_en = 'If a solid right circular cylinder is sliced by an oblique plane cutting through its lateral sides without intersecting the bases, what geometric shape is formed at the cross section?';
        correctAns_ar = 'قطع ناقص (شكل بيضاوي - Ellipse)';
        correctAns_en = 'Ellipse';
        explanation_ar = 'قطع الأسطوانة بمستوى مائل بزاوية حادة غير عمودية على المحور يولد قطعًا ناقصًا (شكل بيضاوي أملس تام).';
        explanation_en = 'An oblique plane intersecting the lateral cylinder surface without meeting the circular bases produces an exact mathematical ellipse.';
      } else if (solid === 'cone_horizontal') {
        questionText_ar = 'إذا تم قطع مخروط دائري قائم بمستوى أفقي موازٍ تمامًا لقاعدته الدائرية، فما هو شكل المقطع العرضي الناتج؟';
        questionText_en = 'If a right circular cone is sliced by a horizontal plane perfectly parallel to its base, what is the exact cross-sectional shape?';
        correctAns_ar = 'دائرة (Circle)';
        correctAns_en = 'Circle';
        explanation_ar = 'أي مقطع مستوٍ موازٍ لقاعدة المخروط الدائري القائم يقطع المخروط في دائرة مصغرة متحدة المركز مع القاعدة.';
        explanation_en = 'Any planar cross section parallel to the base of a right circular cone yields a circle.';
      } else {
        questionText_ar = 'إذا قُطع مكعب مصمت بمستوى يمر برؤوسه الثلاثة المجاورة لنفس الرأس، فما هو شكل المقطع العرضي المتكون؟';
        questionText_en = 'If a solid cube is sliced by a plane passing through the three vertices adjacent to a single corner, what polygon cross section is formed?';
        correctAns_ar = 'مثلث متطابق الأضلاع (Equilateral Triangle)';
        correctAns_en = 'Equilateral Triangle';
        explanation_ar = 'المستوى يقطع الأوجه الثلاثة المتعامدة على مسافات متساوية، مشكلاً أوتارًا متساوية الطول، مما ينتج مثلثًا متطابق الأضلاع تمامًا.';
        explanation_en = 'The cutting plane intersects three orthogonal faces at equidistant intervals, forming three equal hypotenuse segments and producing an equilateral triangle.';
      }

      const distractors = [
        { ar: 'مستطيل (Rectangle)', en: 'Rectangle' },
        { ar: 'شبه منحرف (Trapezoid)', en: 'Trapezoid' },
        { ar: 'قطع مكافئ (Parabola)', en: 'Parabola' }
      ].filter(d => d.ar !== correctAns_ar);

      const all = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors.slice(0, 3).map(d => ({ ar: d.ar, en: d.en, isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: it.ar,
        text_en: it.en,
        value: it.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar,
        questionText_en,
        options,
        correctIndex,
        explanation_ar: `${explanation_ar} الخيار الصحيح هو (${options[correctIndex].id}).`,
        explanation_en: `${explanation_en} The definitive option is (${options[correctIndex].id}).`
      };
    }
  },

  // 7. Oblique 45-Degree Mirror Reflection
  {
    id: 'spatial_reflection_oblique_plane',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Shape positioned in Top-Left, reflected across diagonal mirror line y = -x
      // (x, y) reflected across y = -x becomes (-y, -x)
      const drawShape = (rot) => (s) => DrawShapes.arrow(s * 0.8, rot, '#38bdf8');

      // Stimulus: Arrow pointing Up (rot = 0). Across y = -x, Up points Left (rot = -90 / 270)
      const startRot = prng.pick([0, 90, 180, 270]);
      // True 45-degree diagonal reflection transforms rot -> (270 - rot + 360) % 360
      const correctRot = (270 - startRot + 360) % 360;

      const drawMirrorStimulus = (size = 180) => {
        let out = '';
        out += `<rect x="10" y="10" width="${size - 20}" height="${size - 20}" rx="8" fill="none" stroke="currentColor" stroke-width="1.8"/>`;
        // Diagonal mirror line from bottom-left to top-right
        out += `<line x1="10" y1="${size - 10}" x2="${size - 10}" y2="10" stroke="#ec4899" stroke-width="2.5" stroke-dasharray="4,3"/>`;
        // Arrow in top-left region
        out += `<g transform="translate(${size * 0.3}, ${size * 0.3})">${drawShape(startRot)(36)}</g>`;
        out += `<text x="${size * 0.72}" y="${size * 0.75}" text-anchor="middle" font-size="24" font-weight="700" fill="#38bdf8">?</text>`;
        return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-mirror', 'Diagonal Mirror Reflection');
      };

      const distractors = [
        (correctRot + 90) % 360,
        (correctRot + 180) % 360,
        (correctRot + 270) % 360
      ];

      const all = prng.shuffle([correctRot, ...distractors]);
      const correctIndex = all.indexOf(correctRot);

      const options = all.map((rot, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawShape(rot), 100, `Arrow rot ${rot}`),
        value: rot
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تنعكس الإشارة السهمية عبر خط المرآة المائل القطري بزاوية 45 درجة (الممثل بالخط الوردي المتقطع). أي من الخيارات التالية يمثل الاتجاه الصحيح لانعكاس السهم في النصف الآخر؟',
        questionText_en: 'The arrow reflects across the 45-degree diagonal mirror line (pink dashed line). Which option displays the exact reflected orientation in the target quadrant?',
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${drawMirrorStimulus(180)}</div>`,
        options,
        correctIndex,
        explanation_ar: `الانعكاس عبر الخط القطري يبدل المحاور المكانية مع تغيير الإشارة، مما يحول اتجاه السهم إلى الزاوية ${correctRot}° بدقة، وهو ما يطابق الخيار (${options[correctIndex].id}).`,
        explanation_en: `Diagonal reflection interchanges orthogonal coordinate vectors, reorienting the arrow precisely to ${correctRot}°, matching option (${options[correctIndex].id}).`
      };
    }
  },

  // 8. 3D Wireframe Depth Occlusion (Necker Cube Front/Back)
  {
    id: 'spatial_wireframe_depth',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Wireframe with one solid intersecting pillar in front of another
      const isBlueFront = prng.pick([true, false]);

      const drawIntersect = (blueInFront, size = 160) => {
        let out = '';
        const cx = size / 2;
        const cy = size / 2;
        if (blueInFront) {
          // Pink horizontal behind
          out += `<rect x="${cx - 60}" y="${cy - 12}" width="120" height="24" rx="4" fill="#ec4899" stroke="currentColor" stroke-width="1.8"/>`;
          // Blue vertical in front
          out += `<rect x="${cx - 12}" y="${cy - 60}" width="24" height="120" rx="4" fill="#38bdf8" stroke="currentColor" stroke-width="1.8"/>`;
        } else {
          // Blue vertical behind
          out += `<rect x="${cx - 12}" y="${cy - 60}" width="24" height="120" rx="4" fill="#38bdf8" stroke="currentColor" stroke-width="1.8"/>`;
          // Pink horizontal in front
          out += `<rect x="${cx - 60}" y="${cy - 12}" width="120" height="24" rx="4" fill="#ec4899" stroke="currentColor" stroke-width="1.8"/>`;
        }
        return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-depth', 'Depth Occlusion');
      };

      const options = [
        { id: 'A', text_ar: 'العمود الأزرق الرأسي يقع في الأمام ويحجب الوردي', text_en: 'The blue vertical bar is in front and occludes the pink bar', value: 'blue_front' },
        { id: 'B', text_ar: 'العمود الوردي الأفقي يقع في الأمام ويحجب الأزرق', text_en: 'The pink horizontal bar is in front and occludes the blue bar', value: 'pink_front' },
        { id: 'C', text_ar: 'العمودان يقعان في نفس المستوى تمامًا دون حجب', text_en: 'Both bars occupy the exact same plane without occlusion', value: 'coplanar' },
        { id: 'D', text_ar: 'الرؤية متعامدة ولا يمكن تحديد العمق النسبي', text_en: 'Orthogonal perspective precludes depth determination', value: 'unknown' }
      ];

      const correctIndex = isBlueFront ? 0 : 1;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'بناءً على دلالات الحجب البصري والتراكب الهندسي في الشكل ثلاثي الأبعاد، أي من العبارات التالية تصف الترتيب الدقيق للعمق بين العمودين؟',
        questionText_en: 'Based on geometric occlusion cues in the 3D diagram, which statement accurately defines the depth ordering between the two bars?',
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${drawIntersect(isBlueFront, 170)}</div>`,
        options,
        correctIndex,
        explanation_ar: `خطوط الحواف المستمرة وغير المقطوعة تنتمي إلى العنصر الأقرب للناظر في المقدمة، وهو ما يثبت صحة العبارة في الخيار (${options[correctIndex].id}).`,
        explanation_en: `Continuous boundary strokes delineate the foreground occluding object, strictly validating option (${options[correctIndex].id}).`
      };
    }
  },

  // 9. Interlocking 3D Polycube Complementary Fit
  {
    id: 'spatial_interlocking_puzzle_fit',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Solid 2x2x2 cube has 8 mini-cubes. Piece A has 5 mini-cubes, Piece B must have 3 mini-cubes
      const pieceACount = 5;
      const targetComplement = 8 - pieceACount; // 3 cubes

      const options = [
        { id: 'A', text_ar: 'قطعة مكونة من 3 مكعبات صغيرة على شكل حرف L', text_en: 'A 3-cube L-shaped polycube piece', value: 3 },
        { id: 'B', text_ar: 'قطعة مكونة من 4 مكعبات على شكل مربع مسطح', text_en: 'A 4-cube flat square polycube', value: 4 },
        { id: 'C', text_ar: 'قطعة مكونة من مكعبين صغيرين متصلين', text_en: 'A 2-cube linear domino piece', value: 2 },
        { id: 'D', text_ar: 'قطعة مكونة من 5 مكعبات متدرجة', text_en: 'A 5-cube stepped polycube', value: 5 }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `قطعة مجسمة ثلاثية الأبعاد مشكلة من 5 مكعبات صغيرة متلاصقة. ما هي القطعة المكملة هندسيًا التي تتداخل معها تمامًا لتكوين مكعب مصمت كامل بأبعاد 2×2×2 بدون فراغات؟`,
        questionText_en: `A 3D polycube assembly contains 5 unit cubes. Which complementary piece interlocks with it perfectly to form a solid 2x2x2 cube with zero voids?`,
        options,
        correctIndex,
        explanation_ar: `حجم المكعب الكامل 2×2×2 = 8 مكعبات صغيرة. بما أن القطعة الأولى تحجز 5 مكعبات، فإن القطعة المكملة يجب أن تتكون حتمًا وبدقة من 8 - 5 = 3 مكعبات بتشكيل فراغي مطابق، وهو ما يمثله الخيار (${options[correctIndex].id}).`,
        explanation_en: `A solid 2x2x2 cube comprises 8 unit cells. Subtracting the 5 existing cells mandates a complementary piece of exactly 3 cells, isolating (${options[correctIndex].id}).`
      };
    }
  },

  // 10. Triangular Pyramid (Tetrahedron) Net Folding
  {
    id: 'spatial_pyramid_net_fold',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const symbols = ['●', '▲', '■', '★'];
      const chosen = prng.shuffle(symbols);
      const [baseSym, sLeft, sRight, sTop] = chosen;

      // Net of tetrahedron: Central triangle (base) surrounded by 3 triangular flaps
      const drawTetraNet = (size = 180) => {
        let out = '';
        const cx = size / 2;
        const cy = size / 2 + 10;
        const s = 48;
        const h = s * 0.866;

        // Central Base (pointing up)
        out += `<polygon points="${cx},${cy - h * 0.6} ${cx + s / 2},${cy + h * 0.4} ${cx - s / 2},${cy + h * 0.4}" fill="#38bdf8" fill-opacity="0.2" stroke="currentColor" stroke-width="1.8"/>`;
        out += `<text x="${cx}" y="${cy + 5}" text-anchor="middle" font-size="14" font-weight="700" fill="currentColor">${baseSym}</text>`;

        // Top Flap (pointing down above)
        out += `<polygon points="${cx},${cy - h * 0.6} ${cx - s / 2},${cy - h * 1.6} ${cx + s / 2},${cy - h * 1.6}" fill="#818cf8" fill-opacity="0.2" stroke="currentColor" stroke-width="1.8"/>`;
        out += `<text x="${cx}" y="${cy - h - 2}" text-anchor="middle" font-size="14" font-weight="700" fill="currentColor">${sTop}</text>`;

        // Left Flap
        out += `<polygon points="${cx - s / 2},${cy + h * 0.4} ${cx - s},${cy - h * 0.6} ${cx},${cy - h * 0.6}" fill="#ec4899" fill-opacity="0.2" stroke="currentColor" stroke-width="1.8"/>`;
        out += `<text x="${cx - s * 0.5}" y="${cy - 4}" text-anchor="middle" font-size="14" font-weight="700" fill="currentColor">${sLeft}</text>`;

        // Right Flap
        out += `<polygon points="${cx + s / 2},${cy + h * 0.4} ${cx + s},${cy - h * 0.6} ${cx},${cy - h * 0.6}" fill="#10b981" fill-opacity="0.2" stroke="currentColor" stroke-width="1.8"/>`;
        out += `<text x="${cx + s * 0.5}" y="${cy - 4}" text-anchor="middle" font-size="14" font-weight="700" fill="currentColor">${sRight}</text>`;

        return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-tetra', 'Tetrahedron Net');
      };

      const options = [
        { id: 'A', text_ar: `الأوجه الثلاثة المجاورة لقاعدة (${baseSym}) هي (${sLeft} و ${sRight} و ${sTop}) وتلتقي عند رأس مشترك واحد`, text_en: `The three faces adjacent to base (${baseSym}) are (${sLeft}, ${sRight}, ${sTop}) converging at a single apex`, value: 'valid' },
        { id: 'B', text_ar: `الوجه (${sLeft}) يقع في الجهة المقابلة تمامًا للوجه (${sRight}) ولن يشتركا في أي حافة`, text_en: `Face (${sLeft}) lies strictly opposite (${sRight}) sharing no edge`, value: 'opposite' },
        { id: 'C', text_ar: `الوجه (${sTop}) يصبح هو القاعدة السفلية وتظل باقي الأوجه متباعدة`, text_en: `Face (${sTop}) becomes the lower base while remaining faces stay planar`, value: 'wrong_base' },
        { id: 'D', text_ar: `الشبكة لا يمكن أن تنطوي هندسيًا لتشكيل هرم منتظم`, text_en: `The net cannot fold into a regular polyhedron`, value: 'invalid_net' }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `عند طي شبكة الهرم رباعي السطوح (مجسم رباعي الوجوه Tetrahedron) الموضحة في الرسم لتكوين الهرم ثلاثي الأبعاد، أي الاستنتاجات الفضائية التالية صحيحة بالضرورة؟`,
        questionText_en: `When folding the regular tetrahedron net shown into a 3D solid, which spatial deduction is strictly true?`,
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${drawTetraNet(200)}</div>`,
        options,
        correctIndex,
        explanation_ar: `في الهرم رباعي الوجوه، ترتبط الأوجه الجانبية الثلاثة بحواف القاعدة المركزية (${baseSym}) وتنطوي للأعلى لتلتقي رؤوسها الخارجية الثلاثة عند قمة مشتركة واحدة، مما يثبت صحة الخيار (${options[correctIndex].id}).`,
        explanation_en: `Folding triangular flaps around the central base brings all three external vertices together at a single top apex, strictly confirming (${options[correctIndex].id}).`
      };
    }
  },

  // 11. Cylindrical Surface Unfolding
  {
    id: 'spatial_cylinder_unfolding',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const radius = prng.int(3, 7);
      const height = prng.int(8, 15);

      const options = [
        { id: 'A', text_ar: `مستطيل بأبعاد (${height}) ارتفاعًا و (${2 * radius}π) عرضًا، بالإضافة لدائرتين نصف قطر كل منهما (${radius})`, text_en: `A rectangle of dimensions (${height}) by (${2 * radius}π), with two circles of radius (${radius})`, value: 'correct' },
        { id: 'B', text_ar: `مربع بأبعاد (${height}) في (${height}) وقرص دائري واحد`, text_en: `A square of height by height and one circular base`, value: 'square' },
        { id: 'C', text_ar: `شكل بيضاوي متصل بدائرتين علوية وسفلية`, text_en: `An ellipse connected to two circular bases`, value: 'ellipse' },
        { id: 'D', text_ar: `مستطيل عرضه (${radius}π) وقاعدتان مثلثتان`, text_en: `A rectangle of width radius*pi and two triangular bases`, value: 'triangle' }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا تم فرد ونشر السطح الخارجي الكامل لأسطوانة دائرية قائمة مصمتة نصف قطر قاعدتها (${radius}) وارتفاعها (${height}) على سطح مستوٍ، ما هي الأشكال الهندسية الناتجة وأبعادها الدقيقة؟`,
        questionText_en: `Unfolding the complete exterior surface net of a solid cylinder with base radius (${radius}) and height (${height}) produces which exact geometric figures and dimensions?`,
        options,
        correctIndex,
        explanation_ar: `السطح الجانبي المنحني للأسطوانة ينفرد إلى مستطيل: عرضه يطابق محيط القاعدة الدائرية (2πr = ${2 * radius}π) وارتفاعه يطابق ارتفاع الأسطوانة (${height})، بالإضافة إلى القاعدتين الدائريتين العلوية والسفلية بنصف قطر (${radius}). الخيار الصحيح هو (${options[correctIndex].id}).`,
        explanation_en: `The lateral cylinder mantle unfolds into a rectangle whose length matches the circular base perimeter (2*pi*r) and whose height matches the cylinder height, accompanied by the two circular base caps, matching (${options[correctIndex].id}).`
      };
    }
  },

  // 12. 3D Cube Face Traversal Path
  {
    id: 'spatial_traversal_maze_3d',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Shortest path along edges from one corner to diagonally opposite corner: 3 edges
      const edgeCount = 3;

      const options = [
        { id: 'A', text_ar: '3 حواف متتالية متعامدة', text_en: '3 consecutive orthogonal edges', value: 3 },
        { id: 'B', text_ar: '2 حواف فقط', text_en: '2 edges only', value: 2 },
        { id: 'C', text_ar: '4 حواف على الأقل', text_en: 'At least 4 edges', value: 4 },
        { id: 'D', text_ar: '6 حواف كاملة', text_en: '6 edges', value: 6 }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'نملة تتحرك على حواف مكعب ثلاثي الأبعاد حصريًا. ما هو أقل عدد ممكن من الحواف المستقيمة التي يجب أن تقطعها النملة للانتقال من رأس معين إلى الرأس المقابل له قطريًا في أقصى الزاوية البعيدة من المكعب؟',
        questionText_en: 'An ant crawls strictly along the 12 wireframe edges of a 3D cube. What is the minimum number of edge traversals required to travel from a starting vertex to the diametrically opposite vertex?',
        options,
        correctIndex,
        explanation_ar: `إحداثيات الرأس المقابل قطريًا تبعد خطوة واحدة على كل من المحاور الفضائية الثلاثة (X, Y, Z). للوصول إليه يجب قطع حافة واحدة على الأقل على طول كل بُعد، مما يتطلب 1 + 1 + 1 = 3 حواف متعامدة، وهو ما يحدده الخيار (${options[correctIndex].id}).`,
        explanation_en: `Reaching the diametrically opposite coordinate across 3-space mandates traversing at least one unit step along the X, Y, and Z axes, establishing 3 edges as the minimal path in (${options[correctIndex].id}).`
      };
    }
  },

  // 13. Regular Octahedron Net Face Adjacency
  {
    id: 'spatial_octahedron_net',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: '8 أوجه مثلثة متطابقة الأضلاع و 6 رؤوس و 12 حافة', text_en: '8 equilateral triangular faces, 6 vertices, and 12 edges', value: 'valid' },
        { id: 'B', text_ar: '6 أوجه مربعة و 8 رؤوس و 12 حافة', text_en: '6 square faces, 8 vertices, and 12 edges', value: 'cube' },
        { id: 'C', text_ar: '10 أوجه مثلثة و 8 رؤوس و 16 حافة', text_en: '10 triangular faces, 8 vertices, and 16 edges', value: 'ten' },
        { id: 'D', text_ar: '8 أوجه خماسية و 12 رأسًا', text_en: '8 pentagonal faces and 12 vertices', value: 'pents' }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'المجسم الأفلاطوني المنتظم ثماني السطوح (Regular Octahedron) يتكون عند طي شبكته الفضائية من أي مجموعة من العناصر الهندسية الدقيقة؟',
        questionText_en: 'Folding the planar net of a regular platonic octahedron creates a 3D polyhedron characterized by which exact topological parameters?',
        options,
        correctIndex,
        explanation_ar: `المجسم ثماني السطوح المنتظم يتألف من 8 أوجه مثلثة متطابقة الأضلاع، تلتقي عند 6 رؤوس وترتبط بـ 12 حافة، محققًا معادلة أويلر (V - E + F = 6 - 12 + 8 = 2). الخيار المطابق هو (${options[correctIndex].id}).`,
        explanation_en: `A regular octahedron comprises 8 equilateral triangular faces, 6 vertices, and 12 edges, satisfying Euler characteristic V - E + F = 6 - 12 + 8 = 2, confirming (${options[correctIndex].id}).`
      };
    }
  },

  // 14. Orthogonal Shadow Silhouette Projection
  {
    id: 'spatial_shadow_projection',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // A hollow cylinder illuminated from the side produces a rectangular shadow
      const options = [
        { id: 'A', text_ar: 'مستطيل مصمت (Solid Rectangle)', text_en: 'Solid Rectangle', value: 'rect' },
        { id: 'B', text_ar: 'دائرة مفرغة (Hollow Circle)', text_en: 'Hollow Circle', value: 'circle' },
        { id: 'C', text_ar: 'مثلث متساوي الساقين (Isosceles Triangle)', text_en: 'Isosceles Triangle', value: 'triangle' },
        { id: 'D', text_ar: 'قطع ناقص (Ellipse)', text_en: 'Ellipse', value: 'ellipse' }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'إذا سقطت أشعة ضوء شمسية متوازية أفقيًا على أسطوانة رأسية قائمة قائمة على حائط رأسي خلفها، فما هو شكل الظل الهندسي المرتسم على الحائط بدقة؟',
        questionText_en: 'If collimated horizontal parallel light rays illuminate a vertical cylinder against a perpendicular back wall, what exact geometric shadow silhouette is cast on the wall?',
        options,
        correctIndex,
        explanation_ar: `المسقط العمودي الجانبي للأسطوانة يحدد بحدود ارتفاعها الرأسي وقطرها الأفقي، مما يسقط ظلاً هندسيًا مستطيل الشكل تمامًا، وهو ما يطابق الخيار (${options[correctIndex].id}).`,
        explanation_en: `Orthogonal side projection bounded by cylinder height and diameter casts a rectilinear rectangular silhouette, verifying (${options[correctIndex].id}).`
      };
    }
  },

  // 15. Torus Planar Cut Topology
  {
    id: 'spatial_torus_slice_topology',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'دائرتان منفصلتان متباعدتان (Two disjoint concentric/separated circles)', text_en: 'Two disjoint separated circles', value: 'circles' },
        { id: 'B', text_ar: 'شكل حلزوني مفتوح لا نهائي', text_en: 'An open infinite helix', value: 'helix' },
        { id: 'C', text_ar: 'مستطيلان متقاطعان بزاوية قائمة', text_en: 'Two intersecting perpendicular rectangles', value: 'rectangles' },
        { id: 'D', text_ar: 'نقطة تماس واحدة فقط', text_en: 'A single tangency point', value: 'point' }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'إذا قُطع مجسم حلقة الكعكة الدائرية المجوفة (Torus) بمستوى رأسي يمر عموديًا بمنتصف أنبوب الحلقة على جانبين متقابلين، فما هو الشكل الهندسي للمقطع العرضي الناتج؟',
        questionText_en: 'If a geometric torus is sliced by a vertical plane cutting perpendicularly through the tube at two opposite points, what cross-sectional geometry is produced?',
        options,
        correctIndex,
        explanation_ar: `المستوى الرأسي يقطع أنبوب الحلقة في موضعين منفصلين، مما يولد مقطعين دائريين كاملين متباعدين، وهو ما يطابق الخيار (${options[correctIndex].id}).`,
        explanation_en: `The transverse planar slice intersects both tube cross sections symmetrically, yielding two disjoint circles in option (${options[correctIndex].id}).`
      };
    }
  }
];
